import React, { useState } from 'react';
import { Search, ChevronDown, Download } from 'lucide-react';

export default function List() {
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: '',
    country: '',
    status: '',
    branch: '',
    entity: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sample data with Entity ID and Entity Name
  const orders = [
    {
      id: 'ORD-23401',
      user: 'John Smith',
      orderDate: '2023-04-15',
      country: 'Sri Lanka',
      originType: 'L/Adult',
      branch: 'Colombo Branch',
      status: 'Purchased',
      voidDate: '2025-10-31',
      entityId: 'ENT-001',
      entityName: 'Global Travels Ltd'
    },
    {
      id: 'ORD-23400',
      user: 'Emma Johnson',
      orderDate: '2023-04-15',
      country: 'India',
      originType: 'F/Child',
      branch: 'Mumbai Branch',
      status: 'Voided',
      voidDate: '2023-04-16',
      entityId: 'ENT-002',
      entityName: 'Pacific Tours Inc'
    },
    {
      id: 'ORD-23398',
      user: 'Ravi Kumar',
      orderDate: '2023-04-14',
      country: 'Sri Lanka',
      originType: 'L/Child',
      branch: 'Kandy Branch',
      status: 'Expired',
      voidDate: '2023-04-21',
      entityId: 'ENT-001',
      entityName: 'Global Travels Ltd'
    }
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    console.log('Applying filters:', filters);
    // Filter logic would go here
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      startDate: '',
      endDate: '',
      country: '',
      status: '',
      branch: '',
      entity: ''
    });
  };

  const handleViewOrder = (orderId) => {
    // Navigate to OrderDetailsComponent with the order ID
    window.location.href = `/OrderDetailsComponent?orderId=${orderId}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Purchased': 'bg-emerald-50 text-emerald-700',
      'Voided': 'bg-red-50 text-red-700',
      'Expired': 'bg-amber-50 text-amber-700',
      'Processing': 'bg-brand-blue/10 text-brand-blue'
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Order List</h1>
        <div className="mt-1 text-sm text-slate-500">
          Home <span className="mx-1.5 text-slate-400">/</span>
          Merchant Dashboard <span className="mx-1.5 text-slate-400">/</span>
          <span className="font-semibold text-slate-700">Order List</span>
        </div>
      </div>

      {/* Filter Section */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-900">Filter Orders</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Search */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Order ID or user..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none"
                />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">Start Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none"
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">End Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {/* Country */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">Country</label>
              <div className="relative">
                <select
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none appearance-none bg-white"
                >
                  <option value="">Select country</option>
                  <option value="sri-lanka">Sri Lanka</option>
                  <option value="india">India</option>
                  <option value="usa">USA</option>
                  <option value="uk">UK</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Order Status */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">Order Status</label>
              <div className="relative">
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none appearance-none bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="voided">Voided</option>
                  <option value="expired">Expired</option>
                  <option value="purchased">Purchased</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Branch */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">Branch</label>
              <div className="relative">
                <select
                  value={filters.branch}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none appearance-none bg-white"
                >
                  <option value="">Select branch</option>
                  <option value="colombo">Colombo Branch</option>
                  <option value="kandy">Kandy Branch</option>
                  <option value="mumbai">Mumbai Branch</option>
                  <option value="delhi">Delhi Branch</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Entity */}
            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700">Entity</label>
              <div className="relative">
                <select
                  value={filters.entity}
                  onChange={(e) => handleFilterChange('entity', e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none appearance-none bg-white"
                >
                  <option value="">Select entity</option>
                  <option value="ent-001">Global Travels Ltd</option>
                  <option value="ent-002">Pacific Tours Inc</option>
                  <option value="ent-003">Eastern Airlines</option>
                  <option value="ent-004">Western Travel Co</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleClearFilters}
              className="px-6 py-2.5 text-brand-blue hover:text-brand-blue-dark font-medium"
            >
              Clear Filters
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-6 py-2.5 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-dark font-medium transition-colors"
            >
              Apply Filters
            </button>
            <button className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium flex items-center gap-2 transition-colors">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-slate-50 border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    User
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Entity ID
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Entity Name
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Order Date
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Country
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Order Status
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Void Date
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-brand-blue font-medium">{order.id}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {order.user}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-teal-600 font-medium">{order.entityId}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {order.entityName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-500">
                      {order.orderDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {order.country}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-700">
                      {order.branch}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-500">
                      {order.voidDate || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button 
                        onClick={() => handleViewOrder(order.id)}
                        className="text-brand-blue hover:text-brand-blue-dark font-medium hover:underline transition-all"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <div className="text-sm text-slate-500">
              Showing 1-3 of 120 orders
            </div>
            <div className="flex items-center gap-2">
              <button className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                Less than
              </button>
              <button className="px-4 py-2 bg-brand-blue text-white rounded-lg">1</button>
              <button className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">2</button>
              <button className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">3</button>
              <span className="px-3">...</span>
              <button className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">40</button>
              <button className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                Greater than
              </button>
              <select className="ml-4 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-blue/30 focus:border-transparent outline-none">
                <option>10</option>
                <option>25</option>
                <option>50</option>
                <option>100</option>
              </select>
              <span className="text-sm text-slate-500 ml-2">per page</span>
            </div>
          </div>
        </div>
    </div>
  );
}